import { Fragment, useMemo, useRef, useState, useEffect } from "react";
import { MapContainer, Marker, Tooltip, useMap, useMapEvents } from "react-leaflet";
import VectorBasemapLayer from "@/components/VectorBasemapLayer";
import L from "leaflet";

// Wide bounds covering UK + USA + Canada. Loose so the user can pan freely
// between regions without hitting an invisible wall.
const WORLD_BOUNDS = [
  [12, -170],
  [78, 30],
];
const DEFAULT_CENTER = [52.6, -1.3];

// Pixel radius used for clustering at the current zoom. Lowered from 55 so
// individual avatar markers stay separate longer (only truly overlapping
// pins merge into a numbered cluster).
const CLUSTER_RADIUS_PX = 28;
// Horizontal spacing between adjacent avatars in the expanded row.
const FAN_SPACING_PX = 150;
// When the pointer is farther than this from the cluster centre while a
// cluster is expanded, collapse the fan. Scales with the row's half-width.
const FAN_COLLAPSE_MARGIN_PX = 80;
// Orange used when a cluster contains BOTH male and female contacts.
const MIX_GENDER_COLOR = "#FF8C00";

function hexToRgba(hex, alpha = 1) {
  let h = (hex || "#F5D300").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const num = parseInt(h, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function escapeAttr(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function initialsOf(name) {
  return (name || "")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Town-only overlap separation (visual only — never touches stored lat/lng).
 *
 * When 2+ contacts share exactly the same geocoded coordinates (typically
 * because they were geocoded only to a town/city centroid, without a street
 * or postcode) they stack on the same pixel and only one avatar is visible.
 * This helper produces a Map<id, { lat, lng }> of *display* positions —
 * deterministic (sorted by id, fixed angle) so the same contact always lands
 * on the same visual spot and never drifts between reloads.
 *
 * Radius is intentionally tiny (~0.004° ≈ 445 m at UK latitudes) so a
 * coastal-town centroid can NEVER be pushed visually into the sea. Any pin
 * with a precise geocode (street/postcode) will have a unique lat/lng, will
 * not share a bucket with others, and therefore renders at its true point
 * with zero artificial offset.
 */
const OVERLAP_ROUND_DECIMALS = 4; // ~11 m — treat closer than this as identical
const OVERLAP_RING_RADIUS_DEG = 0.004; // ~445 m — small enough to stay on land

function computeOverlapDisplayCoords(contacts) {
  const groups = new Map();
  if (!contacts) return new Map();
  for (const c of contacts) {
    if (!Number.isFinite(c.lat) || !Number.isFinite(c.lng)) continue;
    const key = `${c.lat.toFixed(OVERLAP_ROUND_DECIMALS)}|${c.lng.toFixed(
      OVERLAP_ROUND_DECIMALS
    )}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  const out = new Map();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    group.sort((a, b) => String(a.id).localeCompare(String(b.id)));
    const N = group.length;
    for (let i = 0; i < N; i++) {
      const c = group[i];
      const angle = (2 * Math.PI * i) / N + Math.PI / 4; // rotate for aesthetics
      const dLat = OVERLAP_RING_RADIUS_DEG * Math.sin(angle);
      // Scale longitude by 1/cos(lat) so the visual ring stays roughly
      // circular even at higher latitudes.
      const cosLat = Math.max(0.1, Math.cos((c.lat * Math.PI) / 180));
      const dLng = (OVERLAP_RING_RADIUS_DEG * Math.cos(angle)) / cosLat;
      out.set(c.id, { lat: c.lat + dLat, lng: c.lng + dLng });
    }
  }
  return out;
}

function buildIcon({ selected = false, temp = false, color = "#F5D300", kind = "contact", upcoming = false, avatar = null } = {}) {
  const ringColor = temp ? "#00E5FF" : color;
  const coreColor = temp ? "#00E5FF" : selected ? "#00E5FF" : color;
  const glowRgba = hexToRgba(ringColor, 0.85);
  const selRing = selected ? "0 0 22px " + hexToRgba("#00E5FF", 1) : "0 0 14px " + glowRgba;
  const selBorder = selected ? "#00E5FF" : ringColor;

  // Contact with an upcoming booking — keep the normal glowing dot but add
  // expanding sonar rings that ripple outward so it stands out on the map.
  if (kind === "contact" && upcoming && !temp) {
    // Booked contacts get a slightly bigger avatar so they stand out even
    // more (in addition to the pulsing rings).
    if (avatar) {
      return L.divIcon({
        className: "",
        html: `
          <div class="contact-avatar-wrap booking-marker" style="width:44px;height:44px;">
            <span class="booking-ping" style="border-color:${hexToRgba(coreColor, 0.9)};"></span>
            <span class="booking-ping booking-ping-delay" style="border-color:${hexToRgba(coreColor, 0.9)};"></span>
            <div class="contact-avatar-face" style="width:44px;height:44px;background-image:url('${avatar.replace(/'/g, "%27")}');border-color:${coreColor};box-shadow:0 0 12px ${hexToRgba(coreColor, 0.9)};"></div>
          </div>
        `,
        iconSize: [44, 44],
        iconAnchor: [22, 22],
      });
    }
    return L.divIcon({
      className: "",
      html: `
        <div class="contact-marker-wrap booking-marker">
          <span class="booking-ping" style="border-color:${hexToRgba(coreColor, 0.9)};"></span>
          <span class="booking-ping booking-ping-delay" style="border-color:${hexToRgba(coreColor, 0.9)};"></span>
          <div class="contact-marker-ring" style="border-color:${hexToRgba(selBorder, 0.6)};box-shadow:${selRing};"></div>
          <div class="contact-marker-core" style="background:${coreColor};box-shadow:0 0 10px ${hexToRgba(coreColor, 0.95)};"></div>
        </div>
      `,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
  }

  // POIs use a diamond shape (a square rotated 45deg) to clearly distinguish
  // them from circular contact pins. No ring — just the diamond core.
  if (kind === "poi") {
    return L.divIcon({
      className: "",
      html: `
        <div class="poi-marker-wrap">
          <div class="poi-marker-core" style="background:${coreColor};box-shadow:0 0 10px ${hexToRgba(
        coreColor,
        0.95
      )};"></div>
        </div>
      `,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
  }
  // Plain contact — show the actual face/avatar as the marker when we have
  // one, so the user sees who's where at every zoom level. Falls back to the
  // colored glowing dot for contacts without an uploaded photo.
  if (avatar && kind === "contact" && !temp) {
    return L.divIcon({
      className: "",
      html: `
        <div class="contact-avatar-wrap">
          <div class="contact-avatar-face" style="background-image:url('${avatar.replace(/'/g, "%27")}');border-color:${coreColor};box-shadow:0 0 10px ${hexToRgba(coreColor, 0.85)};"></div>
        </div>
      `,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });
  }
  return L.divIcon({
    className: "",
    html: `
      <div class="contact-marker-wrap">
        <div class="contact-marker-core" style="background:${coreColor};box-shadow:0 0 10px ${hexToRgba(
      coreColor,
      0.95
    )};"></div>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function buildClusterIcon(members, color) {
  const n = members.length;
  const ringRgba = hexToRgba(color, 0.6);
  const glowRgba = hexToRgba(color, 0.85);
  const html = `
    <div class="cluster-dot-wrap">
      <div class="cluster-dot-ring" style="border-color:${ringRgba};box-shadow:0 0 18px ${glowRgba};"></div>
      <div class="cluster-dot" style="background:${color};color:#0a0a0a;box-shadow:0 0 14px ${hexToRgba(
        color,
        0.95
      )};">${n}</div>
    </div>
  `;
  return L.divIcon({
    className: "",
    html,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

function pickClusterColor(members, contactGender, contactColors, defaultColor = "#F5D300") {
  if (contactGender) {
    const genders = new Set();
    for (const m of members) {
      const g = contactGender.get(m.id);
      if (g) genders.add(g);
    }
    if (genders.has("male") && genders.has("female")) {
      return MIX_GENDER_COLOR;
    }
  }
  return contactColors?.get(members[0].id) || defaultColor;
}

function buildFanAvatarIcon(c, contactColors) {
  const color = contactColors?.get(c.id) || "#F5D300";
  const initials = initialsOf(c.name);
  const avatarHtml = c.avatar
    ? `<div class="fan-avatar" style="border-color:${color};box-shadow:0 0 18px ${hexToRgba(
        color,
        0.85
      )};background-image:url('${escapeAttr(c.avatar)}');"></div>`
    : `<div class="fan-avatar fan-avatar-text" style="border-color:${color};color:${color};box-shadow:0 0 18px ${hexToRgba(
        color,
        0.6
      )};">${escapeAttr(initials || "·")}</div>`;
  const labelHtml = `<div class="fan-label" style="border-color:${hexToRgba(
    color,
    0.45
  )};color:#fff;">${escapeAttr(c.name || "Unnamed")}</div>`;
  return L.divIcon({
    className: "",
    html: `<div class="fan-wrap">${avatarHtml}${labelHtml}</div>`,
    iconSize: [220, 156],
    iconAnchor: [110, 52], // anchor at avatar center
  });
}

function FlyTo({ target, onDone }) {
  const map = useMap();
  // Only re-fly when the target's numeric position actually changes. Without
  // this, any parent re-render that reused the same flyTarget state object
  // would refire the flyTo and snap the map back after the user panned.
  const lastKeyRef = useRef(null);
  useEffect(() => {
    if (!target || target.lat == null || target.lng == null) return;
    const key = `${target.lat.toFixed(6)}|${target.lng.toFixed(6)}|${target.zoom || 13}|${target.token || ""}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    map.flyTo([target.lat, target.lng], target.zoom || 13, { duration: 1.0 });
    // Let the parent clear its flyTarget state so future data refreshes
    // don't accidentally refire this on remount.
    if (typeof onDone === "function") {
      setTimeout(() => onDone(), 1100);
    }
  }, [target, map, onDone]);
  return null;
}

/**
 * On FIRST successful contact load, fit the map viewport to show every pin.
 * Fixes the original "I created a Canadian contact but the map still shows
 * Europe" complaint — a UK-centred default was hiding pins in other
 * countries. Only runs once per mount AND only if the user hasn't panned/
 * zoomed manually yet — so it never fights user gestures.
 */
function FitBoundsOnLoad({ points, userInteractedRef }) {
  const map = useMap();
  const doneRef = useRef(false);
  useEffect(() => {
    if (doneRef.current) return;
    if (userInteractedRef && userInteractedRef.current) {
      doneRef.current = true; // user beat us — never auto-fit
      return;
    }
    if (!points || points.length === 0) return;
    const valid = points.filter(
      (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)
    );
    if (valid.length === 0) return;
    doneRef.current = true;
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 12, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(valid.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 12, animate: false });
  }, [points, map, userInteractedRef]);
  return null;
}

/**
 * Tracks whether the user has manually interacted with the map. As soon as
 * they drag/scroll-zoom/pinch/double-click, subsequent auto-recentering
 * effects (FitBoundsOnLoad) are disabled. Explicit navigation (FlyTo target,
 * "Show on map" button) still works — that's user-initiated.
 */
function UserInteractionWatcher({ interactedRef }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !interactedRef) return;
    const markInteracted = () => {
      interactedRef.current = true;
    };
    // dragstart fires on any pan. zoomstart fires on scroll-wheel / +/- keys /
    // pinch / double-click. These are always user-initiated (programmatic
    // flyTo doesn't dispatch them until later, and by then we've already
    // gated FitBoundsOnLoad — safe).
    map.on("dragstart", markInteracted);
    map.on("mousedown", markInteracted);
    map.on("wheel", markInteracted);
    map.on("touchstart", markInteracted);
    return () => {
      map.off("dragstart", markInteracted);
      map.off("mousedown", markInteracted);
      map.off("wheel", markInteracted);
      map.off("touchstart", markInteracted);
    };
  }, [map, interactedRef]);
  return null;
}

function ClickHandler({ onMapClick, onMapBgClick }) {
  useMapEvents({
    click(e) {
      // Always notify so we can collapse expanded cluster
      if (typeof onMapBgClick === "function") onMapBgClick();
      if (typeof onMapClick === "function") {
        onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    },
  });
  return null;
}

/**
 * Fires onContextMenu({ lat, lng, clientX, clientY }) for:
 *   - Desktop right-click anywhere on the map
 *   - iPad / touch: long-press (~600 ms) without dragging
 * Suppresses the browser default right-click menu so the app's own popover
 * can be shown instead.
 */
function ContextMenuHandler({ onContextMenu }) {
  const map = useMap();
  useMapEvents({
    contextmenu(e) {
      if (typeof onContextMenu === "function") {
        onContextMenu({
          lat: e.latlng.lat,
          lng: e.latlng.lng,
          clientX: e.originalEvent.clientX,
          clientY: e.originalEvent.clientY,
        });
      }
    },
  });

  useEffect(() => {
    if (!map) return;
    const container = map.getContainer();
    const blockNative = (e) => e.preventDefault();
    container.addEventListener("contextmenu", blockNative);

    // Long-press to fire context menu on touch devices (iPad)
    let pressTimer = null;
    let startX = 0;
    let startY = 0;
    let cancelled = false;

    const clearTimer = () => {
      if (pressTimer) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      cancelled = false;
      clearTimer();
      pressTimer = setTimeout(() => {
        if (cancelled) return;
        const rect = container.getBoundingClientRect();
        const layerPoint = L.point(t.clientX - rect.left, t.clientY - rect.top);
        const latlng = map.containerPointToLatLng(layerPoint);
        if (typeof onContextMenu === "function") {
          onContextMenu({
            lat: latlng.lat,
            lng: latlng.lng,
            clientX: t.clientX,
            clientY: t.clientY,
          });
        }
      }, 600);
    };
    const onTouchMove = (e) => {
      if (!pressTimer || !e.touches || !e.touches[0]) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (dx * dx + dy * dy > 144) {
        cancelled = true;
        clearTimer();
      }
    };
    const onTouchEnd = () => {
      cancelled = true;
      clearTimer();
    };

    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: true });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    container.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("contextmenu", blockNative);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
      clearTimer();
    };
  }, [map, onContextMenu]);

  return null;
}

/**
 * Watches the browser's geolocation and renders a pulsing blue marker (with
 * a direction arrow when a heading is available) at the user's position.
 * Exposes a small "locate me" button anchored to the map container.
 */
function UserLocationLayer() {
  const map = useMap();
  const [pos, setPos] = useState(null); // { lat, lng, heading, accuracy }
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);
  const watchIdRef = useRef(null);
  const didFlyRef = useRef(false);

  // Heading from DeviceOrientation (iOS Safari requires user-gesture permission)
  const [compassHeading, setCompassHeading] = useState(null);
  const orientHandlerRef = useRef(null);

  const stop = () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (orientHandlerRef.current) {
      window.removeEventListener("deviceorientation", orientHandlerRef.current);
      window.removeEventListener("deviceorientationabsolute", orientHandlerRef.current);
      orientHandlerRef.current = null;
    }
    setActive(false);
    setPos(null);
    setCompassHeading(null);
    didFlyRef.current = false;
  };

  const start = async () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported on this device.");
      return;
    }
    setError(null);
    setActive(true);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const next = {
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          heading: Number.isFinite(p.coords.heading) ? p.coords.heading : null,
          accuracy: p.coords.accuracy || 0,
        };
        setPos(next);
        if (!didFlyRef.current) {
          didFlyRef.current = true;
          map.flyTo([next.lat, next.lng], Math.max(map.getZoom(), 13), {
            duration: 1.0,
          });
        }
      },
      (err) => {
        setError(err.message || "Location unavailable");
        setActive(false);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    // Compass heading (iOS needs permission via user gesture)
    const onOrient = (ev) => {
      // webkitCompassHeading is degrees clockwise from North (iOS)
      let h = null;
      if (typeof ev.webkitCompassHeading === "number") {
        h = ev.webkitCompassHeading;
      } else if (typeof ev.alpha === "number") {
        // alpha is degrees counter-clockwise from North; invert to clockwise
        h = 360 - ev.alpha;
      }
      if (h != null && Number.isFinite(h)) setCompassHeading(h);
    };
    orientHandlerRef.current = onOrient;

    try {
      if (
        typeof DeviceOrientationEvent !== "undefined" &&
        typeof DeviceOrientationEvent.requestPermission === "function"
      ) {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res === "granted") {
          window.addEventListener("deviceorientation", onOrient);
        }
      } else {
        window.addEventListener("deviceorientationabsolute", onOrient);
        window.addEventListener("deviceorientation", onOrient);
      }
    } catch {
      // Permission denied or not available — heading just won't update
    }
  };

  useEffect(() => {
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = () => {
    if (active && pos) {
      // Re-center on current position
      map.flyTo([pos.lat, pos.lng], Math.max(map.getZoom(), 14), {
        duration: 0.8,
      });
    } else if (active) {
      stop();
    } else {
      start();
    }
  };

  // Build the marker icon. Use compass heading first (more reliable for
  // standing still), fall back to GPS heading if moving.
  const heading =
    compassHeading != null
      ? compassHeading
      : pos && pos.heading != null
      ? pos.heading
      : null;

  const userIcon = useMemo(() => {
    const arrow =
      heading != null
        ? `<div class="user-loc-arrow" style="transform: translate(-50%, -100%) rotate(${heading}deg);"></div>`
        : "";
    return L.divIcon({
      className: "",
      html: `
        <div class="user-loc-wrap">
          <div class="user-loc-pulse"></div>
          <div class="user-loc-dot"></div>
          ${arrow}
        </div>
      `,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
  }, [heading]);

  return (
    <>
      {pos && (
        <Marker
          position={[pos.lat, pos.lng]}
          icon={userIcon}
          interactive={false}
          zIndexOffset={1000}
        />
      )}
      <div className="leaflet-top leaflet-right" style={{ pointerEvents: "none" }}>
        <div className="leaflet-control" style={{ pointerEvents: "auto", marginRight: 10, marginTop: 80 }}>
          <button
            type="button"
            onClick={handleClick}
            title={active ? "Recenter on my location" : "Show my location"}
            data-testid="map-locate-me-btn"
            className={`locate-btn ${active ? "is-active" : ""}`}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M12 2v3"></path>
              <path d="M12 19v3"></path>
              <path d="M2 12h3"></path>
              <path d="M19 12h3"></path>
            </svg>
          </button>
          {error && (
            <div className="locate-error" data-testid="map-locate-error">{error}</div>
          )}
        </div>
      </div>
    </>
  );
}


/**
 * Renders contacts as either single markers or clusters with a fan-expand
 * behaviour. Uses screen-space pixel projection at the current zoom for
 * stable, predictable grouping.
 */
function ClusteredMarkers({
  contacts,
  contactColors,
  contactGender,
  selectedId,
  onMarkerClick,
  onMarkerDragEnd,
  expandedCluster,
  setExpandedCluster,
  kind = "contact",
  defaultColor = "#F5D300",
  idPrefix = "c",
  upcomingIds,
  meetingBadgesById,
  ageByContact,
  videoContactIds,
}) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());

  // Keep the cluster-projection zoom in sync with the map's real zoom.
  // `zoomend` alone misses programmatic fitBounds({animate:false}) (used by
  // FitBoundsOnLoad on first paint) — that dispatches `moveend`/`viewreset`
  // but not `zoomend`, so without these extra handlers the cluster grouping
  // math projects at the stale mount-zoom and over-clusters the landing view.
  useMapEvents({
    zoomend: () => {
      setZoom(map.getZoom());
      setExpandedCluster(null);
    },
    moveend: () => {
      const z = map.getZoom();
      setZoom((prev) => (prev === z ? prev : z));
    },
    viewreset: () => setZoom(map.getZoom()),
    movestart: () => setExpandedCluster(null),
    mouseout: (e) => {
      // Only collapse when the pointer actually leaves the map container,
      // not when it transitions between markers inside the map.
      const related = e.originalEvent?.relatedTarget;
      const container = map.getContainer();
      if (!related || !container.contains(related)) {
        setExpandedCluster(null);
      }
    },
    mousemove: (e) => {
      if (!expandedCluster) return;
      const centerPx = map.latLngToContainerPoint([
        expandedCluster.lat,
        expandedCluster.lng,
      ]);
      const pointerPx = map.mouseEventToContainerPoint(e.originalEvent);
      const N = expandedCluster.count || 2;
      const halfWidth = ((N - 1) * FAN_SPACING_PX) / 2;
      // Allow generous margin around the horizontal row.
      const maxDx = halfWidth + FAN_COLLAPSE_MARGIN_PX;
      const maxDy = FAN_COLLAPSE_MARGIN_PX;
      const dx = Math.abs(centerPx.x - pointerPx.x);
      const dy = Math.abs(centerPx.y - pointerPx.y);
      if (dx > maxDx || dy > maxDy) {
        setExpandedCluster(null);
      }
    },
  });

  // Visual-only offset for contacts stacked on identical geocoded coords
  // (e.g. multiple town-only pins sharing the same city centroid). Recomputes
  // whenever the contact set actually changes. NEVER writes back to props /
  // storage — real lat/lng stays intact.
  const displayCoords = useMemo(
    () => computeOverlapDisplayCoords(contacts),
    [contacts]
  );

  const clusters = useMemo(() => {
    if (!contacts || contacts.length === 0) return [];
    const pts = contacts
      .filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng))
      .map((c) => {
        const disp = displayCoords.get(c.id);
        const lat = disp ? disp.lat : c.lat;
        const lng = disp ? disp.lng : c.lng;
        return { c, lat, lng, px: map.project([lat, lng], zoom) };
      });

    const used = new Array(pts.length).fill(false);
    const out = [];
    for (let i = 0; i < pts.length; i++) {
      if (used[i]) continue;
      used[i] = true;
      const group = [pts[i]];
      for (let j = i + 1; j < pts.length; j++) {
        if (used[j]) continue;
        const dx = pts[i].px.x - pts[j].px.x;
        const dy = pts[i].px.y - pts[j].px.y;
        if (dx * dx + dy * dy < CLUSTER_RADIUS_PX * CLUSTER_RADIUS_PX) {
          group.push(pts[j]);
          used[j] = true;
        }
      }
      out.push(group);
    }
    return out;
  }, [contacts, zoom, map, displayCoords]);

  return (
    <>
      {clusters.map((group) => {
        if (group.length === 1) {
          const c = group[0].c;
          const pos = [group[0].lat, group[0].lng];
          const color = contactColors?.get(c.id) || defaultColor;
          return (
            <Marker
              key={`${idPrefix}-${c.id}`}
              position={pos}
              draggable={true}
              icon={buildIcon({
                selected: selectedId === c.id,
                color,
                kind,
                upcoming: !!upcomingIds?.has(c.id),
                avatar: kind === "contact" ? c.avatar : null,
              })}
              eventHandlers={{
                click: () => onMarkerClick?.(c),
                dragend: (e) => {
                  const latlng = e.target.getLatLng();
                  onMarkerDragEnd?.(c, { lat: latlng.lat, lng: latlng.lng });
                },
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -16]}
                opacity={1}
                permanent={false}
                className="contact-marker-tip"
              >
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  {c.avatar ? (
                    <img
                      src={c.avatar}
                      alt=""
                      style={{
                        width: 61,
                        height: 61,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: `2.5px solid ${color}`,
                        boxShadow: `0 0 12px ${color}AA`,
                        imageRendering: "auto",
                      }}
                      draggable={false}
                    />
                  ) : (
                    <div
                      style={{
                        width: 61,
                        height: 61,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.06)",
                        border: `2.5px solid ${color}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color,
                        fontFamily: "Rajdhani, sans-serif",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        fontSize: 22,
                      }}
                    >
                      {initialsOf(c.name) || "·"}
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "Rajdhani, sans-serif",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          fontWeight: 600,
                          color: "#fff",
                          fontSize: 22,
                          lineHeight: 1.1,
                        }}
                      >
                        {c.name}
                      </div>
                      {ageByContact?.get(c.id) && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: 9999,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: "#f5d300",
                            border: "1px solid rgba(245,211,0,0.55)",
                            background: "rgba(245,211,0,0.10)",
                          }}
                        >
                          {ageByContact.get(c.id)}
                        </span>
                      )}
                      {(meetingBadgesById?.get(c.id) || []).map((b, i) => (
                        <span
                          key={i}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: 9999,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: b.color,
                            border: `1px solid ${b.color}99`,
                            background: `${b.color}1F`,
                            boxShadow: `0 0 8px ${b.color}55`,
                          }}
                        >
                          {b.name}
                        </span>
                      ))}
                      {videoContactIds?.has(c.id) && (
                        <span
                          title="Will video"
                          data-testid={`tooltip-video-${c.id}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "2px 8px",
                            borderRadius: 9999,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: "#00e5ff",
                            border: "1px solid rgba(0,229,255,0.55)",
                            background: "rgba(0,229,255,0.10)",
                          }}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 7l-7 5 7 5V7z"/>
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                          </svg>
                          Video
                        </span>
                      )}
                    </div>
                    {(c.town || c.region) && (
                      <div
                        style={{
                          fontFamily: "JetBrains Mono, monospace",
                          color: "rgba(255,255,255,0.5)",
                          fontSize: 15,
                          marginTop: 4,
                        }}
                      >
                        {[c.town, c.region].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
              </Tooltip>
            </Marker>
          );
        }

        // ---- cluster of 2+ ----
        const members = group.map((p) => p.c);
        const clusterId = `${idPrefix}-${members
          .map((m) => m.id)
          .sort()
          .join("|")}`;
        const isExpanded = expandedCluster?.id === clusterId;

        // Weighted centre = average of display coords (falls back to real
        // coords for pins that were not offset for overlap).
        const centerLat =
          group.reduce((s, p) => s + p.lat, 0) / group.length;
        const centerLng =
          group.reduce((s, p) => s + p.lng, 0) / group.length;

        const dotColor = pickClusterColor(members, contactGender, contactColors, defaultColor);

        if (!isExpanded) {
          return (
            <Marker
              key={`cluster-${idPrefix}-${clusterId}`}
              position={[centerLat, centerLng]}
              icon={buildClusterIcon(members, dotColor)}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stopPropagation(e);
                  setExpandedCluster({
                    id: clusterId,
                    lat: centerLat,
                    lng: centerLng,
                    count: members.length,
                  });
                },
                mouseover: () =>
                  setExpandedCluster({
                    id: clusterId,
                    lat: centerLat,
                    lng: centerLng,
                    count: members.length,
                  }),
              }}
              zIndexOffset={500}
            >
              <Tooltip
                direction="top"
                offset={[0, -10]}
                opacity={1}
                permanent={false}
                className="contact-marker-tip"
              >
                <div
                  style={{
                    fontFamily: "Rajdhani, sans-serif",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#fff",
                    fontSize: 12,
                  }}
                >
                  {members.length} contacts here — hover or tap to expand
                </div>
              </Tooltip>
            </Marker>
          );
        }

        // Expanded: render fan of avatar markers around the centre.
        const centerPx = map.project([centerLat, centerLng], zoom);
        const N = members.length;
        return (
          <Fragment key={`cluster-exp-${clusterId}`}>
            {/* invisible anchor so the centre is still on the map for distance maths */}
            <Marker
              position={[centerLat, centerLng]}
              icon={L.divIcon({
                className: "",
                html: `<div class="cluster-anchor"></div>`,
                iconSize: [20, 20],
                iconAnchor: [10, 10],
              })}
              interactive={false}
              zIndexOffset={400}
            />
            {members.map((m, idx) => {
              // Lay avatars out in a horizontal row centred on the cluster.
              const totalWidth = (N - 1) * FAN_SPACING_PX;
              const fx = centerPx.x - totalWidth / 2 + idx * FAN_SPACING_PX;
              const fy = centerPx.y;
              const ll = map.unproject(L.point(fx, fy), zoom);
              return (
                <Marker
                  key={`fan-${clusterId}-${m.id}`}
                  position={[ll.lat, ll.lng]}
                  icon={buildFanAvatarIcon(m, contactColors)}
                  zIndexOffset={1000}
                  eventHandlers={{
                    click: (e) => {
                      L.DomEvent.stopPropagation(e);
                      onMarkerClick?.(m);
                      setExpandedCluster(null);
                    },
                  }}
                />
              );
            })}
          </Fragment>
        );
      })}
    </>
  );
}

export default function MapView({
  contacts,
  contactColors,
  contactGender,
  meetingBadgesById,
  ageByContact,
  videoContactIds,
  pois,
  poiColors,
  showPois = false,
  tempPin,
  flyTarget,
  onFlyDone,
  fitAllVersion, // increment to trigger a fit-all to visible contacts
  selectedId,
  onMapClick,
  onMarkerClick,
  onMarkerDragEnd,
  onPoiClick,
  onPoiDragEnd,
  onContextMenu,
  upcomingContactIds,
}) {
  const mapRef = useRef(null);
  const userInteractedRef = useRef(false);
  const [expandedCluster, setExpandedCluster] = useState(null);
  const lastFitVersionRef = useRef(0);

  const recenter = () => {
    userInteractedRef.current = false; // treat this as explicit re-fit
    const map = mapRef.current;
    if (!map) return;
    const all = [...(contacts || []), ...(pois || [])].filter(
      (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)
    );
    if (all.length === 0) return;
    if (all.length === 1) {
      map.setView([all[0].lat, all[0].lng], 12, { animate: true });
      return;
    }
    const b = L.latLngBounds(all.map((p) => [p.lat, p.lng]));
    map.fitBounds(b, { padding: [60, 60], maxZoom: 12, animate: true });
  };

  // Explicit fit trigger from parent (e.g. "Show N AI results on map").
  useEffect(() => {
    if (fitAllVersion == null) return;
    if (fitAllVersion === lastFitVersionRef.current) return;
    lastFitVersionRef.current = fitAllVersion;
    // Defer one tick so `contacts` prop update propagates before we fit.
    setTimeout(recenter, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitAllVersion]);

  return (
    <div data-testid="map-container" style={{ height: "100%", width: "100%" }}>
      <MapContainer
        ref={mapRef}
        center={DEFAULT_CENTER}
        zoom={5}
        minZoom={2}
        maxZoom={18}
        maxBounds={WORLD_BOUNDS}
        maxBoundsViscosity={0.3}
        worldCopyJump={true}
        zoomControl={true}
        attributionControl={true}
        style={{ height: "100%", width: "100%" }}
      >
        <VectorBasemapLayer />
        <ClickHandler
          onMapClick={onMapClick}
          onMapBgClick={() => setExpandedCluster(null)}
        />
        <ContextMenuHandler onContextMenu={onContextMenu} />
        <UserInteractionWatcher interactedRef={userInteractedRef} />
        <FlyTo target={flyTarget} onDone={onFlyDone} />
        <FitBoundsOnLoad
          points={[
            ...(contacts || []),
            ...(showPois ? pois || [] : []),
          ]}
          userInteractedRef={userInteractedRef}
        />
        <UserLocationLayer />

        <ClusteredMarkers
          contacts={contacts}
          contactColors={contactColors}
          contactGender={contactGender}
          selectedId={selectedId}
          onMarkerClick={onMarkerClick}
          onMarkerDragEnd={onMarkerDragEnd}
          expandedCluster={expandedCluster}
          setExpandedCluster={setExpandedCluster}
          kind="contact"
          defaultColor="#F5D300"
          idPrefix="c"
          upcomingIds={upcomingContactIds}
          meetingBadgesById={meetingBadgesById}
          ageByContact={ageByContact}
          videoContactIds={videoContactIds}
        />

        {showPois && pois && pois.length > 0 && (
          <ClusteredMarkers
            contacts={pois}
            contactColors={poiColors}
            contactGender={null}
            selectedId={null}
            onMarkerClick={onPoiClick}
            onMarkerDragEnd={onPoiDragEnd}
            expandedCluster={expandedCluster}
            setExpandedCluster={setExpandedCluster}
            kind="poi"
            defaultColor="#00d4a0"
            idPrefix="p"
          />
        )}

        {tempPin && (
          <Marker
            position={[tempPin.lat, tempPin.lng]}
            icon={buildIcon({ temp: true })}
            interactive={false}
          />
        )}
      </MapContainer>
      {/* Explicit "Fit all pins" control — user always keeps manual control,
          this is the ONE deliberate way to re-fit the viewport. */}
      <button
        type="button"
        onClick={recenter}
        title="Fit all pins"
        aria-label="Fit all pins"
        className="absolute z-[1000] top-3 right-3 h-9 w-9 rounded-md bg-black/70 border border-white/15 text-[#f5d300] hover:bg-black/90 hover:text-white flex items-center justify-center shadow-lg"
        data-testid="map-recenter-btn"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 3 21 3 21 9"></polyline>
          <polyline points="9 21 3 21 3 15"></polyline>
          <line x1="21" y1="3" x2="14" y2="10"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        </svg>
      </button>
    </div>
  );
}
